import { OrderColumn } from '@components/OrderDispatch/OrderGrid/OrderColumn'
import { checkRunout, isOrderRequestV1 } from '@components/OrderDispatch/util'
import { defaultDateDef } from '@components/_SiteWide/CellTemplates/typeColumnDefs'

import { AnalyticIcon } from '@components/StoreMonitoring/StoreDashboard/TankAlertDisplay/components/AnalyticIcon'
import { addCommasToNumber } from '@gravitate-js/excalibrr'
import { getDisplayTimezoneString } from '@utils/general'
import { formatters } from '@utils/intl/dateTimeFormatters'
import type {
  ColDef,
  Grid,
  GridApi,
  ICellRendererParams,
  IRowNode,
  RowNode,
  ValueFormatterParams,
  ValueGetterParams,
} from 'ag-grid-community'
import moment from 'moment'
import type React from 'react'
import { forwardRef } from 'react'

import type { OrderState } from '@/components/OrderMovements/OrderMovements/MovementDetail/BOL/Detail/constants'
import { startCase } from 'lodash'
import { TimeDeadlineFilter, noFitOptions } from '../Filters/TimeDeadline'
import { doesOrderNoFitFilterPass, doesOrderRunoutFilterPass } from '../Filters/TimeDeadline/customFilters'
import { OrderWindowTag, formatTimeWindow } from '@/components/_SiteWide/OrderDisplay/OrderTimeFormats'
import { loadingWindowComparator } from '@/components/OrderDispatch/OrderGrid/OrderRow'
import {
  RunoutCellRenderer,
  NoFitCellRenderer,
  contractCellRenderer,
  DeliveryWindowCellRenderer,
} from './OrderColumnCellrenderers'
import { DeliveryWindowComparator, RunoutComparator } from './OrderColumnComparators'
import type { PurchaseOrderColumnProps, GridOrder } from './types'
import { shouldHideNoFitRunout } from './util'
import { DeliveryWindowCellEditor } from './DeliveryWindowCellEditor'
import { CarrierWindowEndColumn, CarrierWindowStartColumn } from './CarrierDispatchColDefs'
import { getComparator } from '@/utils/grid'

export const lpWindowColumnDefs = [
  {
    headerName: 'LP Window',
    hide: true,
    valueGetter: (params: ValueGetterParams) => {
      const start = params?.data?.lp_start
      const end = params?.data?.lp_end
      const timezone = params?.data?.lp_start_local
      return `${start ? formatters.MONTH_DATE_TIME(moment(start), timezone) : ''} - ${
        end ? formatters.MONTH_DATE_TIME(moment(end), timezone) : ''
      }`
    },
    field: 'lp_window',
    minWidth: 180,
    comparator: (
      a: Gravitate.Order,
      b: Gravitate.Order,
      nodeA: RowNode<Gravitate.Order>,
      nodeB: RowNode<Gravitate.Order>
    ) => {
      const retain1 = nodeA?.data?.lp_start
      const retain2 = nodeB?.data?.lp_start
      const now = new Date().toISOString()
      if ((retain1 ?? '') < now && (retain2 ?? '') < now) {
        const runout1 = nodeA?.data?.lp_end
        const runout2 = nodeB?.data?.lp_end
        const orderBool = (runout1 ?? '') > (runout2 ?? '')
        return orderBool ? 1 : -1
      }
      return (retain1 ?? '') > (retain2 ?? '') ? 1 : -1
    },
  },
  {
    headerName: 'LP Start',
    field: 'lp_start',
    valueGetter: (params: ValueGetterParams) => {
      return params.data?.lp_start
        ? formatters.MONTH_DATE_TIME(moment(params.data?.lp_start), params?.data?.lp_start_local)
        : ''
    },
    hide: true,
    suppressFiltersToolPanel: true,
    minWidth: 220,
  },
  {
    headerName: 'LP End',
    field: 'lp_end',
    valueGetter: (params: ValueGetterParams) =>
      params.data?.lp_end ? formatters.MONTH_DATE_TIME(moment(params?.data.lp_end), params?.data?.lp_end_local) : '',
    hide: true,
    suppressFiltersToolPanel: true,
    minWidth: 220,
  },
]

export function getPriceFlagColumnDefs({ handleOrderChange }: { handleOrderChange: (order: Gravitate.Order) => void }) {
  return [
    {
      headerName: 'Price Flag',
      field: 'price_flag',
      cellRenderer: 'priceFlagRenderer',
      sortable: false,
      cellRendererParams: { handleOrderChange },
      valueGetter: (params: ValueGetterParams<Gravitate.Order>) => params?.data?.price_flag,
      enableRowGroup: true,
      flex: 2.5,
      initialHide: true,
    },
    {
      headerName: 'Flag Name',
      field: 'price_flag.name',
      valueGetter: (params: ValueGetterParams<Gravitate.Order>) => params?.data?.price_flag?.name,
      enableRowGroup: true,
      flex: 4,
      initialHide: true,
    },
    {
      headerName: 'Flag Reason Code',
      field: 'price_flag.reason_code',
      valueGetter: (params: ValueGetterParams<Gravitate.Order>) => params?.data?.price_flag?.reason_code,
      enableRowGroup: true,
      flex: 4,
      initialHide: true,
    },
    {
      headerName: 'Flag Updated By',
      field: 'price_flag.updated_by',
      valueGetter: (params: ValueGetterParams<Gravitate.Order>) => params?.data?.price_flag?.updated_by,
      enableRowGroup: true,
      flex: 4,
      initialHide: true,
    },
    {
      headerName: 'Flag Updated Date',
      field: 'price_flag.updated_date',
      cellRenderer: 'flagUpdatedRenderer',
      valueGetter: (params: ValueGetterParams<Gravitate.Order>) => params?.data?.price_flag?.updated,
      enableRowGroup: true,
      flex: 4,
      initialHide: true,
    },
    {
      headerName: 'Flag Price Difference',
      field: 'price_flag.price_difference',
      valueGetter: (params: ValueGetterParams<Gravitate.Order>) => params?.data?.price_flag?.price_difference,
      enableRowGroup: true,
      flex: 4,
      initialHide: true,
    },
    {
      headerName: 'Flag Optimal Terminal',
      field: 'price_flag.optimal_terminal',
      valueGetter: (params: ValueGetterParams<Gravitate.Order>) => params?.data?.price_flag?.optimal_terminal,
      enableRowGroup: true,
      flex: 4,
      initialHide: true,
    },
    {
      headerName: 'Flag Loaded Mile Difference',
      field: 'price_flag.loaded_mile_difference',
      valueGetter: (params: ValueGetterParams<Gravitate.Order>) => params?.data?.price_flag?.loaded_mile_difference,
      enableRowGroup: true,
      flex: 4,
      initialHide: true,
    },
    {
      headerName: 'Flag Notes',
      field: 'price_flag.notes',
      valueGetter: (params: ValueGetterParams<Gravitate.Order>) => params?.data?.price_flag?.notes,
      initialHide: true,
      suppressColumnsToolPanel: true,
      suppressFiltersToolPanel: true,
      minWidth: 220,
    },
  ]
}

export function IdColumn({ showModal }: { showModal?: (orderId: string) => void }) {
  return {
    headerName: '',
    field: 'id',
    cellRenderer: 'buttonsCellRenderer',
    valueGetter: (params: ValueGetterParams<Gravitate.Order>) => params?.data?.id ?? params?.data?.order_id ?? '',
    filter: false,
    sortable: false,
    hide: !showModal,
    cellRendererParams: { showModal },
    minWidth: 130,
  }
}

export function RecievedAtColumn() {
  return {
    headerName: 'Received At',
    cellRenderer: 'carrierRecievedCellRender',
    flex: 1.5,
    field: 'received_at',
  }
}

export function MarketColumn() {
  return {
    headerName: 'Market',
    field: 'market',
    flex: 1,
    initialHide: true,
    enableRowGroup: true,
  }
}

export function SectorColumn() {
  return {
    headerName: 'Sector',
    field: 'sector',
    flex: 1,
    initialHide: true,
    enableRowGroup: true,
  }
}

export function DriverColumn() {
  return {
    field: 'driver',
    initialHide: true,
  }
}

export function DriverStatusColumn({ scopes }: { scopes: Partial<Gravitate.AuthorizationResponse> }) {
  return {
    headerName: 'Driver Status',
    field: 'status',
    cellRenderer: 'dispatchStatusCellRenderer',
    valueGetter: (params: ValueGetterParams<Gravitate.Order>) => params?.data?.trip_status || params?.data?.state,
    enableRowGroup: true,
    cellRendererParams: { carrierDisplay: scopes?.is_carrier, stackCarrierWithDriver: true },
    minWidth: 70,
    cellClass: 'stringType',
    // sort by order.estimated_end earliest to latest
    comparator: (
      _: Gravitate.Order,
      __: Gravitate.Order,
      nodeA: { data: Gravitate.Order },
      nodeB: { data: Gravitate.Order },
      isInverted: boolean
    ) => {
      const orderA = nodeA?.data
      const orderB = nodeB?.data
      const eta1 = moment(orderA?.estimated_end)
      const eta2 = moment(orderB?.estimated_end)

      // Define sorting behavior based on state
      const isCarrierOrder = (order: Gravitate.Order) => !!order?.carrier || !!order?.manager
      const isSortable = (order: Gravitate.Order) =>
        ['assigned', 'in progress'].includes(order?.state) && !isCarrierOrder(order)
      const isCompleted = (order: { state: string }) => order?.state === 'completed'

      const sortableA = isSortable(orderA)
      const sortableB = isSortable(orderB)
      const completedA = isCompleted(orderA)
      const completedB = isCompleted(orderB)
      const carrierA = isCarrierOrder(orderA)
      const carrierB = isCarrierOrder(orderB)

      // Completed orders always go to the bottom
      if (completedA && !completedB) return 1
      if (completedB && !completedA) return -1

      // Carrier orders should be below non-carrier orders (but above completed ones)
      if (!completedA && !completedB) {
        if (carrierA && !carrierB) return 1
        if (carrierB && !carrierA) return -1
      }

      // If both are "assigned" or "in progress", sort by estimated_end
      if (sortableA && sortableB) {
        return eta1.isBefore(eta2) ? -1 : 1
      }

      // If only one is "assigned", it should appear above non-sortable ones
      if (sortableA) return -1
      if (sortableB) return 1

      // Otherwise, maintain the order
      return 0
    },
  }
}

export function HauledByColumn() {
  return {
    headerName: 'Hauled By',
    field: 'hauled_by',
    valueGetter: (params: ValueGetterParams<Gravitate.Order>) => params?.data?.driver || params?.data?.carrier,
    hide: true,
    suppressColumnsToolPanel: true,
    suppressFiltersToolPanel: true,
    cellClass: 'stringType',
  }
}

export function UnresolvedFlagsColumn({ handleOrderChange }: { handleOrderChange: (order: Gravitate.Order) => void }) {
  return {
    headerName: 'Flags',
    field: 'unresolved_flags',
    cellRenderer: 'orderFlagsCellRenderer',
    sortable: false,
    filterValueGetter: (params: ValueGetterParams<Gravitate.Order>) => params?.data?.flags?.map((flag) => flag.name),
    cellRendererParams: { handleOrderChange },
    valueGetter: (params: ValueGetterParams<Gravitate.Order>) => params?.data?.unresolved_flags?.map((flag) => flag),
    enableRowGroup: true,
    maxWidth: 100,
  }
}

export function destinationsComparator(
  a: Gravitate.Order,
  b: Gravitate.Order,
  nodeA: IRowNode<Gravitate.Order>,
  nodeB: IRowNode<Gravitate.Order>,
  isInverted: boolean
) {
  const firstDropLocationA = nodeA?.data?.drops?.[0]
  const firstDropLocationB = nodeB?.data?.drops?.[0]

  const firstDropParsedA = parseInt(firstDropLocationA?.location_name ?? '')
  const firstDropParsedB = parseInt(firstDropLocationB?.location_name ?? '')

  if (Number.isNaN(firstDropParsedA) || Number.isNaN(firstDropParsedB)) {
    return firstDropLocationA?.location_name.localeCompare(firstDropLocationB?.location_name ?? '') ?? 0
  }

  return firstDropParsedA - firstDropParsedB
}

export function DestinationsColumn({ setOrderHistoryStoreId }: { setOrderHistoryStoreId: (storeId: string) => void }) {
  return {
    headerName: 'Destinations',
    autoHeight: true,
    field: 'drops',
    enableRowGroup: true,
    valueGetter: (params: ValueGetterParams<Gravitate.Order>) =>
      params?.data?.drops?.map((drop) => `${drop.location_name}, ${drop.store_name}`),
    cellRenderer: 'destinationsCellRenderer',
    cellRendererParams: {
      setOrderHistoryStoreId,
    },
    minWidth: 100,
    comparator: destinationsComparator,
  }
}

export function LocationNameColumn() {
  return {
    headerName: 'Location Name',
    field: 'location_name',
    valueGetter: (params: { data: { drops: Gravitate.OrderDrop[] } }) =>
      params?.data?.drops?.map((drop) => drop.location_name),
    hide: true,
    suppressColumnsToolPanel: true,
    suppressFiltersToolPanel: true,
  }
}

export function SiteNameColumn() {
  return {
    headerName: 'Site Name',
    field: 'site_name',
    valueGetter: (params: { data: { drops: Gravitate.OrderDrop[] } }) =>
      params?.data?.drops?.map((drop) => drop.store_name),
    hide: true,
    suppressColumnsToolPanel: true,
    suppressFiltersToolPanel: true,
  }
}

export function OriginsColumn() {
  return {
    headerName: 'Origins',
    autoHeight: true,
    field: 'origins',
    valueGetter: (params: ValueGetterParams) =>
      params?.data?.loads?.map((load: Gravitate.OrderLoad) => load.location_name),
    cellRenderer: 'originsCellRenderer',
    enableRowGroup: true,
    minWidth: 140,
  }
}

export function DestinationNameColumn() {
  return {
    field: 'destination_name',
    headerName: 'Dest. Name',
    initialHide: true,
    autoHeight: true,
    valueGetter: (params: ValueGetterParams) =>
      isOrderRequestV1(params?.data)
        ? params?.data?.site_name
        : params?.data?.drops?.map((drop: Gravitate.OrderDrop) => drop.store_name || drop.location_name),
    cellRenderer: (params: ICellRendererParams) =>
      isOrderRequestV1(params?.data)
        ? params?.data?.site_name
        : params?.data?.drops?.map((drop: Gravitate.OrderDrop) => (
            <div key={drop.store_id}>{drop?.store_name || drop?.location_name}</div>
          )),
  }
}

export function DestinationStreetColumn() {
  return {
    field: 'destination_street',
    headerName: 'Dest. Street',
    initialHide: true,
    autoHeight: true,
    valueGetter: (params: { data: Gravitate.Order }) =>
      isOrderRequestV1(params?.data)
        ? params?.data?.site_address
        : params?.data?.drops?.map((drop: Gravitate.OrderDrop) => drop.address),
    cellRenderer: (params: ICellRendererParams) =>
      isOrderRequestV1(params?.data)
        ? params?.data?.site_address
        : params?.data?.drops?.map((drop: Gravitate.OrderDrop) => (
            <div key={drop.store_id}>{drop.address || 'N/A'}</div>
          )),
  }
}

export function DestinationCityColumn() {
  return {
    field: 'destination_city',
    headerName: 'Dest. City',
    initialHide: true,
    autoHeight: true,
    valueGetter: (params: { data: Gravitate.Order }) =>
      isOrderRequestV1(params?.data)
        ? params?.data?.site_city
        : params?.data?.drops?.map((drop: Gravitate.OrderDrop) => drop.city),
    cellRenderer: (params: { data: Gravitate.Order }) =>
      isOrderRequestV1(params?.data)
        ? params?.data?.site_city
        : params?.data?.drops?.map((drop: Gravitate.OrderDrop, index: number) => <div key={index}>{drop.city}</div>),
  }
}
export function DestinationStateColumn() {
  return {
    field: 'destination_state',
    headerName: 'Dest. State',
    initialHide: true,
    autoHeight: true,
    valueGetter: (params: { data: Gravitate.Order }) =>
      isOrderRequestV1(params?.data)
        ? params?.data?.site_state
        : params?.data?.drops?.map((drop: Gravitate.OrderDrop) => drop.state),
    cellRenderer: (params: { data: Gravitate.Order }) =>
      isOrderRequestV1(params?.data)
        ? params?.data?.site_state
        : params?.data?.drops?.map((drop: Gravitate.OrderDrop, index: number) => <div key={index}>{drop.state}</div>),
  }
}
export function DestinationPostalCodeColumn() {
  return {
    field: 'destination_postal_code',
    headerName: 'Dest. Postal Code',
    initialHide: true,
    autoHeight: true,
    valueGetter: (params: ValueGetterParams) =>
      params?.data?.drops?.map((drop: Gravitate.OrderDrop) => drop.postal_code),
    cellRenderer: (params: ICellRendererParams) =>
      params?.data?.drops?.map((drop: Gravitate.OrderDrop, index: number) => <div key={index}>{drop.postal_code}</div>),
  }
}

export function OriginNameColumn() {
  return {
    field: 'origin_name',
    headerName: 'Orig. Name',
    initialHide: true,
    autoHeight: true,
    valueGetter: (params: ValueGetterParams) =>
      params?.data?.loads?.map((load: Gravitate.OrderLoad) => load.location_name),
    cellRenderer: (params: ICellRendererParams) =>
      params?.data?.loads?.map((load: Gravitate.OrderLoad, index: number) => (
        <div key={index}>{load.location_name || 'N/A'}</div>
      )),
  }
}

export function OriginStreetColumn() {
  return {
    field: 'origin_street',
    headerName: 'Orig. Street',
    initialHide: true,
    autoHeight: true,
    valueGetter: (params: ValueGetterParams) => params?.data?.loads?.map((load: Gravitate.OrderLoad) => load.address),
    cellRenderer: (params: ICellRendererParams) =>
      params?.data?.loads?.map((load: Gravitate.OrderLoad, index: number) => (
        <div key={index}>{load.address || 'N/A'}</div>
      )),
  }
}

export function OriginCityColumn() {
  return {
    field: 'origin_city',
    headerName: 'Orig. City',
    initialHide: true,
    autoHeight: true,
    valueGetter: (params: ValueGetterParams) => params?.data?.loads?.map((load: Gravitate.OrderLoad) => load.city),
    cellRenderer: (params: ICellRendererParams) =>
      params?.data?.loads?.map((load: Gravitate.OrderLoad, index: number) => <div key={index}>{load.city}</div>),
  }
}
export function OriginStateColumn() {
  return {
    field: 'origin_state',
    headerName: 'Orig. State',
    initialHide: true,
    autoHeight: true,
    valueGetter: (params: ValueGetterParams) =>
      params?.data?.loads?.map((load: { state: Gravitate.OrderLoad['state'] }) => load.state),
    cellRenderer: (params: ICellRendererParams) =>
      params?.data?.loads?.map((load: Gravitate.OrderLoad, index: number) => <div key={index}>{load.state}</div>),
  }
}
export function OriginPostalCodeColumn() {
  return {
    field: 'origin_postal_code',
    headerName: 'Orig. Postal Code',
    initialHide: true,
    autoHeight: true,
    valueGetter: (params: ValueGetterParams) =>
      params?.data?.loads?.map((load: Gravitate.OrderLoad) => load.postal_code),
    cellRenderer: (params: ICellRendererParams) =>
      params?.data?.loads?.map((load: Gravitate.OrderLoad, index: number) => (
        <div key={index}>{load.postal_code || 'N/A'}</div>
      )),
  }
}

export function ProductsColumn() {
  return {
    headerName: 'Products',
    field: 'products',
    cellRenderer: 'productsCellRenderer',
    valueGetter: (params: ValueGetterParams) =>
      params?.data?.product_ratios?.map((product: Gravitate.OrderProductRatio) => product.name),
    enableRowGroup: true,
    minWidth: 250,
    initialWidth: 400,
  }
}

export function SuppliersColumn(initialHide = true) {
  return {
    headerName: 'Suppliers',
    field: 'suppliers',
    enableRowGroup: true,
    initialHide,
    valueGetter: (params: ValueGetterParams) => {
      if (params?.data?.suppliers) {
        if (params.data.suppliers.length === 1) {
          return params.data.suppliers.map((supplier: Gravitate.Order['suppliers']) => supplier)
        }
        return params.data.suppliers.filter(
          (supplier: string, index: number, array: string[]) => array.indexOf(supplier) === index
        )
      }
      return null
    },
    flex: 4.5,
  }
}

export function TotalVolumeColumn() {
  return {
    headerName: 'Total Volume',
    field: 'product_ratios',
    enableRowGroup: true,
    initialHide: true,
    valueGetter: (params: ValueGetterParams) =>
      addCommasToNumber(
        params?.data?.product_ratios?.reduce(
          (a: number, b: { volume: Gravitate.OrderProductRatio['volume'] }) => a + b.volume,
          0
        )
      ),
    flex: 1.5,
    filter: 'agNumberColumnFilter',
    filterValueGetter: (params: ValueGetterParams) =>
      params?.data?.product_ratios?.reduce(
        (a: number, b: { volume: Gravitate.OrderProductRatio['volume'] }) => a + b.volume,
        0
      ),
    type: 'rightAligned',
  }
}

export function LoadingWindowColumn() {
  return {
    field: 'load_window_start, load_window_end',
    headerName: 'Loading Window',
    filter: 'agSetColumnFilter',
    autoHeight: true,
    filterParams: {
      buttons: ['reset'],
      values: ['Now', 'Before 6PM', 'Before Midnight', 'After Midnight', 'N/A'],
      valueGetter: (params: ValueGetterParams) => {
        if (!params?.data?.load_window_start || !params?.data?.load_window_end) {
          return ['N/A']
        }
        const start = moment(params.data.load_window_start)
        const end = moment(params.data.load_window_end)
        const now = moment().local()
        const nowAtSeven = moment().local().hours(19).minutes(0).seconds(0)
        const tomAtOne = moment().local().add(1, 'd').hours(1).minutes(0).seconds(0)
        const matches = []
        if (now.isAfter(start)) {
          matches.push('Now')
        }
        if (end.isSameOrAfter(now) && end.isSameOrBefore(nowAtSeven)) {
          matches.push('Before 6PM')
        }
        if (end.isSameOrAfter(now) && end.isSameOrAfter(nowAtSeven) && end.isSameOrBefore(tomAtOne)) {
          matches.push('Before Midnight')
        }
        if (start.isSameOrAfter(now) && start.isSameOrBefore(tomAtOne) && start.isAfter(nowAtSeven)) {
          matches.push('After Midnight')
        }

        return matches
      },
    },
    valueGetter: (params: ValueGetterParams<Gravitate.Order>) => params?.data?.load_window_start,
    cellRenderer: (params: ICellRendererParams) => {
      if (params?.data?.type === 'Order Request (Requested)') return 'N/A'
      return (
        <OrderWindowTag
          isLoadingWindow
          window={formatTimeWindow(
            params?.data?.load_window_start,
            params?.data?.load_window_end,
            params?.data?.type?.toLowerCase() !== 'backhaul',
            true,
            params?.data?.load_window_start_local
          )}
        />
      )
    },
    comparator: loadingWindowComparator,
  }
}

export function LoadingWindowStartColumn() {
  return {
    headerName: 'Loading Window Start',
    field: 'loading_start',
    valueGetter: (params: ValueGetterParams<Gravitate.Order>) => params?.data?.load_window_start,
    hide: true,
    suppressColumnsToolPanel: true,
    suppressFiltersToolPanel: true,
    minWidth: 220,
  }
}

export function LoadingWindowEndColumn() {
  return {
    headerName: 'Loading Window End',
    field: 'loading_end',
    valueGetter: (params: ValueGetterParams<Gravitate.Order>) => params?.data?.load_window_end,
    hide: true,
    suppressColumnsToolPanel: true,
    suppressFiltersToolPanel: true,
    minWidth: 220,
  }
}

export function getOrderWindowAlertIcon(alertName: WindowAlertType, threshold?: number) {
  // Rendering retain / runout / delivery window alerts
  return (
    <div style={{ marginRight: '5px' }}>
      <AnalyticIcon title={alertName} threshold={threshold} size='small' />
    </div>
  )
}

export function DeliveryWindowColumn(isDetail?: boolean) {
  return {
    field: 'dispatch_window_start, dispatch_window_end',
    headerName: 'Delivery Window',
    filter: 'agMultiColumnFilter',
    autoHeight: true,
    menuTabs: ['filterMenuTab', 'generalMenuTab'],
    filterParams: {
      filters: [
        {
          title: 'RETAIN',
          filter: 'agSetColumnFilter',
          display: 'subMenu',
          filterParams: {
            buttons: ['reset'],
            showTooltips: true,
            values:
              moment().isAfter(moment().hours(3).minutes(59)) && moment().isBefore(moment().hours(16).minutes(0))
                ? ['Fits Now', 'Fits By AM Shift Today', 'Fits By PM Shift Today']
                : ['Fits Now', 'Fits By PM Shift Today', 'Fits By AM Shift Tomorrow'],
            valueGetter: (params: ValueGetterParams<Gravitate.Order>) => {
              if (!params?.data?.dispatch_window_start) return ''
              const now = moment().local()
              const amShiftToday = now.hours(4).minutes(0)
              const pmShiftToday = now.hours(16).minutes(0)
              const amShiftTomorrow = now.hours(4).minutes(0).add(1, 'd')
              const isAm = now.isAfter(now.hours(3).minutes(59)) && now.isBefore(now.hours(16).minutes(0))
              const start = moment(params.data.dispatch_window_start)
              const matches = []
              if (start.isBefore(moment())) {
                matches.push('Fits Now')
              }
              if (isAm && start.isBefore(amShiftToday)) {
                matches.push('Fits By AM Shift Today')
              }
              if (start.isBefore(pmShiftToday)) {
                matches.push('Fits By PM Shift Today')
              }
              if (!isAm && start.isBefore(amShiftTomorrow)) {
                matches.push('Fits By AM Shift Tomorrow')
              }

              return matches
            },
          },
        },
        {
          title: 'RUNOUT',
          filter: 'agSetColumnFilter',
          display: 'subMenu',
          filterParams: {
            showTooltips: true,
            values: ['12 Hours', '24 Hours', '3 Days', 'Next Week'],
            valueGetter: (params: ValueGetterParams<Gravitate.Order>) => checkRunout(params),
            buttons: ['reset'],
          },
        },
      ],
    },
    valueGetter: (params) => params?.data?.dispatch_window_end,
    cellRenderer: (rowData) => DeliveryWindowCellRenderer(rowData),
    comparator: DeliveryWindowComparator,
    editable: (params: ValueGetterParams<Gravitate.Order>) =>
      !isDetail && params?.data?.state !== 'completed' && (params?.data?.carrier || params?.data?.manager),
    cellEditorPopup: true,
    cellEditor: DeliveryWindowCellEditor,
    valueSetter: (params) => {
      params.data.carrier_window_start = params.newValue?.carrier_window_start
      params.data.carrier_window_end = params.newValue?.carrier_window_end
      return true
    },
  }
}

export function DeliveryWindowStartColumn() {
  return {
    headerName: 'Delivery Window Start',
    field: 'dispatch_start',
    valueGetter: (params: ValueGetterParams<Gravitate.Order>) =>
      formatters.DATE_TIME(
        params?.data?.dispatch_window_start ?? '',
        params?.data?.dispatch_window_start_local?.toString()
      ),
    valueFormatter: (params: ValueFormatterParams<Gravitate.Order>) =>
      formatters.DATE_TIME(
        params?.data?.dispatch_window_start ?? '',
        params?.data?.dispatch_window_start_local?.toString()
      ),
    hide: true,
    suppressColumnsToolPanel: true,
    suppressFiltersToolPanel: true,
    minWidth: 220,
  }
}

export function DeliveryWindowEndColumn() {
  return {
    headerName: 'Delivery Window End',
    field: 'dispatch_end',
    valueGetter: (params: ValueGetterParams<Gravitate.Order>) =>
      formatters.DATE_TIME(
        params?.data?.dispatch_window_end ?? '',
        params?.data?.dispatch_window_end_local?.toString()
      ),
    valueFormatter: (params: ValueFormatterParams<Gravitate.Order>) =>
      formatters.DATE_TIME(
        params?.data?.dispatch_window_end ?? '',
        params?.data?.dispatch_window_end_local?.toString()
      ),
    hide: true,
    suppressColumnsToolPanel: true,
    suppressFiltersToolPanel: true,
    minWidth: 220,
  }
}

export function RunoutColumn(isTankLevel?: boolean) {
  const field = isTankLevel ? 'tank_dispatch_window_end' : 'dispatch_window_end'
  return {
    field,
    headerName: 'Runout',
    headerTooltip: 'The projected time the tank will runout if this order is not delivered.',
    cellRenderer: (rowData: RowNode<GridOrder>) => RunoutCellRenderer(rowData),
    valueGetter: (params: ValueGetterParams<GridOrder>) => params?.data?.[field],
    valueFormatter: (params: ValueFormatterParams<GridOrder>) =>
      formatters.DATE_TIME(params?.data?.[field] ?? '', params?.data?.dispatch_window_end_local?.toString()),
    filter: forwardRef((props, ref) => (
      <TimeDeadlineFilter {...props} ref={ref} doesFilterPass={doesOrderRunoutFilterPass} />
    )),
    comparator: RunoutComparator,
    width: 100,
    flex: 0,
  }
}

export function RunoutDateTimeColumn(format: 'date' | 'time', isTankLevel?: boolean) {
  const field = isTankLevel ? 'tank_dispatch_window_end' : 'dispatch_window_end'
  const formatter = format === 'date' ? formatters.DATE : formatters.TIME
  return {
    headerName: `Runout ${startCase(format)}`,
    field,
    initialFlex: 1,
    initialHide: true,
    filter: forwardRef((props, ref) => (
      <TimeDeadlineFilter {...props} ref={ref} doesFilterPass={doesOrderRunoutFilterPass} />
    )),
    comparator: (a: string, b: string) => getComparator(a, b, format),
    valueGetter: (params: ValueGetterParams<GridOrder>) =>
      !shouldHideNoFitRunout(params?.data) && params.data?.[field]
        ? formatter(params.data?.[field], params.data?.dispatch_window_end_local?.toString())
        : '',
    cellRenderer: (params: ICellRendererParams<GridOrder>) => params.value ?? 'N/A',
  }
}

export function FitDateTimeColumn(format: 'date' | 'time', isTankLevel?: boolean) {
  const field = isTankLevel ? 'tank_dispatch_window_start' : 'dispatch_window_start'
  const formatter = format === 'date' ? formatters.DATE : formatters.TIME
  return {
    headerName: `No Fit ${startCase(format)}`,
    field,
    initialFlex: 1,
    initialHide: true,
    filter: forwardRef((props, ref) => (
      <TimeDeadlineFilter {...props} ref={ref} doesFilterPass={doesOrderNoFitFilterPass} filterOptions={noFitOptions} />
    )),
    comparator: (a: string, b: string) => getComparator(a, b, format),
    valueGetter: (params: ValueGetterParams<GridOrder>) =>
      !shouldHideNoFitRunout(params?.data) && params.data?.[field]
        ? formatter(params.data?.[field], params.data?.dispatch_window_start_local?.toString())
        : '',
    cellRenderer: (params: ICellRendererParams<GridOrder>) => params.value ?? 'N/A',
  }
}

export function NoFitColumn(isTankLevel?: boolean) {
  const field = isTankLevel ? 'tank_dispatch_window_start' : 'dispatch_window_start'
  return {
    headerName: 'Fit Time',
    headerTooltip: 'The earliest time the order will fit into the tank.',
    field,
    cellRenderer: (rowData: RowNode) => NoFitCellRenderer(rowData),
    valueGetter: (params: { data: GridOrder }) => moment(params?.data?.[field]),
    valueFormatter: (params: ValueFormatterParams<GridOrder>) =>
      formatters.DATE_TIME(params?.data?.[field] ?? '', params?.data?.dispatch_window_start_local?.toString()),
    filter: forwardRef((props, ref) => (
      <TimeDeadlineFilter {...props} ref={ref} doesFilterPass={doesOrderNoFitFilterPass} filterOptions={noFitOptions} />
    )),
    comparator: (_: GridOrder, __: GridOrder, nodeA: { data: GridOrder }, nodeB: { data: GridOrder }) =>
      (nodeA?.data?.[field] ?? '') > (nodeB?.data?.[field] ?? '') ? 1 : -1,
    initialFlex: 2,
  }
}

export function PurchaseOrderColumn({
  isPopoverVisible,
  setIsPopoverVisible,
  scopes,
  manualPO,
}: PurchaseOrderColumnProps) {
  return {
    headerName: 'Purchase Order',
    field: 'po',
    cellRenderer: 'poCellRenderer',
    cellRendererParams: {
      isPopoverVisible,
      setIsPopoverVisible,
      canEdit: scopes?.scopes?.includes('o.w') && !!manualPO,
    },
    cellClass: 'stringType',
    initialHide: true,
  }
}

export function UpdatedColumn({ setAuditOrderId }: { setAuditOrderId: (orderId: string) => void }) {
  return {
    headerName: 'Updated',
    cellRenderer: 'updatedTagCellRenderer',
    cellRendererParams: {
      setAuditOrderId,
    },
    valueGetter: (params: ValueGetterParams<Gravitate.Order>) => params?.data?.updated_by,
    comparator: (
      a: Gravitate.Order,
      b: Gravitate.Order,
      nodeA: { data: Gravitate.Order },
      nodeB: { data: Gravitate.Order },
      isInverted: boolean
    ) => {
      if (!nodeA || !nodeB) {
        return -1
      }
      if (nodeA?.data?.updated_on === nodeB?.data?.updated_on) {
        return 0
      }
      if ((nodeA?.data?.updated_on ?? '') > (nodeB?.data?.updated_on ?? '')) {
        return 1
      }
      return -1
    },
    minWidth: getDisplayTimezoneString() ? 155 : 134,
    field: 'updated_on',
    cellClass: 'stringType',
  }
}

export function CreatedByColumn() {
  return {
    headerName: 'Created By',
    field: 'created_by',
    cellClass: 'stringType',
    initialHide: true,
  }
}

export function RejectColumn({
  isCarrierUser,
  gridAPIRef,
}: { isCarrierUser: boolean; gridAPIRef: React.MutableRefObject<GridApi> }) {
  return {
    headerName: 'Reject',
    field: 'state',
    cellRenderer: 'carrierRejectButton',
    cellRendererParams: {
      isCarrierUser,
      gridAPIRef,
    },
    flex: 2,
    hide: !isCarrierUser,
  }
}

export function EstDropTimeColumn() {
  return {
    headerName: 'Est Drop Time',
    field: 'est_drop_time',
    valueGetter: (params: ValueGetterParams<Gravitate.Order>) => params?.data?.estimated_end,
    hide: true,
    suppressColumnsToolPanel: true,
    suppressFiltersToolPanel: true,
    minWidth: 220,
  }
}

export function DeliveryDateColumn() {
  return {
    headerName: 'Delivery Date',
    field: 'delivery_date',
    key: 'delivery_date',
    ...defaultDateDef('delivery_date', formatters.DATE, true, undefined, true),
    initialHide: true,
  }
}

export function DeliveryTimeColumn() {
  return {
    headerName: 'Delivery Time',
    field: 'delivery_time',
    key: 'delivery_time',
    ...defaultDateDef('delivery_date', formatters.TIME, true, null, true),
    filter: false,
    initialHide: true,
    valueGetter: (params: { data: { delivery_date: moment.MomentInput; delivery_date_local: string | undefined } }) =>
      params?.data?.delivery_date &&
      formatters.TIME(moment(params.data.delivery_date), params.data.delivery_date_local),
  }
}

export function BOLDateColumn() {
  return {
    headerName: 'BOL Date',
    field: 'bol_date',
    ...defaultDateDef('bol_date'),
    initialHide: true,
  }
}

export function ContractColumn() {
  return {
    headerName: 'Contract',
    field: 'contracts',
    hide: true,
    cellRenderer: contractCellRenderer,
  }
}

export function DriverNotesColumn() {
  return {
    headerName: 'Driver Notes',
    field: 'driver_note',
    initialHide: true,
    suppressFiltersToolPanel: true,
  }
}

export function LoadNumberColumn() {
  return {
    headerName: 'Load Number',
    field: 'load_numbers',
    initialHide: true,
    valueGetter: (params: ValueGetterParams<Gravitate.Order>) =>
      params?.data?.load_numbers?.length > 0 ? params.data?.load_numbers?.map((num: string) => num) : '',
    cellClass: 'numberType',
    minWidth: 200,
    cellRenderer: (params: ICellRendererParams) => (
      <div style={{ whiteSpace: 'break-spaces', wordWrap: 'break-word' }}>
        {Array.isArray(params.value) ? params.value.join(', ') : params.value}
      </div>
    ),
  }
}

export function SupplyLockedColumn() {
  return {
    headerName: 'Supply Locked',
    field: 'supply_locked',
    valueGetter: (params: ValueGetterParams<Gravitate.Order>) => {
      const order = params?.data as Gravitate.Order
      return order?.supply_locked ? 'Yes' : 'No'
    },
    initialHide: true,
  }
}

export function CustomerColumn() {
  return {
    headerName: 'Customer',
    field: 'customer',
    initialHide: true,
    valueGetter: (params: ValueGetterParams<Gravitate.Order>) => params?.data?.customer || '',
    comparator: (
      a: Gravitate.Order,
      b: Gravitate.Order,
      nodeA: { data: Gravitate.Order },
      nodeB: { data: Gravitate.Order }
    ) => (nodeA?.data?.customer?.toLowerCase() > nodeB?.data?.customer?.toLowerCase() ? 1 : -1),
  }
}

export function PriceTypeColumn(initialHide = true) {
  return {
    headerName: 'Price Type',
    field: 'price_type',
    initialHide,
    valueGetter: (params: ValueGetterParams<Gravitate.Order>) =>
      startCase(params?.data?.price_type) ||
      params?.data?.loads?.map((load) => startCase(load.price_type)).filter(Boolean),
  }
}

export function TrailerColumn() {
  return {
    headerName: 'Trailer',
    field: 'trailer_config',
    valueGetter: (params: ValueGetterParams<Gravitate.Order>) => params?.data?.trailer_config,
    initialHide: true,
    enableRowGroup: true,
    flex: 4,
  }
}

export function UpdatesColumn({
  setChangeId,
}: { setChangeId: React.Dispatch<React.SetStateAction<string | undefined>> }) {
  return {
    headerName: 'Updates',
    field: 'carrier_info?.crossroads_queued_update',
    cellRenderer: 'hasUpdatesCellRenderer',
    cellRendererParams: {
      setChangeId,
    },
    minWidth: 220,
    cellClass: 'stringType',
  }
}

type ActionsColumnProps = {
  triggerTPDModal: () => void
  handleTPDDismissModal: () => void
  scopes: Gravitate.AuthorizationResponse
  orderStatus: OrderState
  gridAPIRef: React.MutableRefObject<Grid>
}

export function ActionsColumn({
  triggerTPDModal,
  handleTPDDismissModal,
  scopes,
  orderStatus,
  gridAPIRef,
}: ActionsColumnProps): ColDef {
  return {
    headerName: 'Actions',
    field: 'actions',
    sortable: false,
    cellRenderer: 'carrierActionsCellRenderer',
    cellRendererParams: {
      triggerTPDModal,
      handleTPDDismissModal,
      scopes,
      orderStatus,
      gridAPIRef,
    },
    flex: 4,
    minWidth: 150,
    pinned: 'right',
  }
}

export function LastSupplyAutoUpdateColumn() {
  return {
    headerName: 'Last Supply Auto Update',
    field: 'last_supply_auto_update',
    initialHide: true,
    valueFormatter: (params: ICellRendererParams<Gravitate.Order>) => {
      return params.value ? formatters.DATE_TIME(params.value) : ''
    },
  }
}

export function PreDropUllage() {
  return {
    headerName: 'Pre-Drop Ullage',
    field: 'ullage',
    valueFormatter: (params: ICellRendererParams<Gravitate.Order>) => {
      return params.value ? addCommasToNumber(params.value) : ''
    },
    type: 'rightAligned',
  }
}

export function ProductColumn() {
  return {
    headerName: 'Product',
    field: 'product',
  }
}

export function VolumeColumn() {
  return {
    headerName: 'Volume',
    field: 'volume',
    valueFormatter: (params: ICellRendererParams<Gravitate.OrderProductRatio>) => {
      return params.value ? addCommasToNumber(params.value) : ''
    },
    type: 'rightAligned',
  }
}

export function DestinationColumn() {
  return {
    headerName: 'Destination',
    field: 'destination',
    valueGetter: (params: ValueGetterParams<Gravitate.Order>) =>
      `${params?.data?.destination}, ${params?.data?.destination_name}`,
  }
}

export function OriginColumn() {
  return {
    headerName: 'Origin',
    field: 'origin',
  }
}

export function TankIdColumn() {
  return {
    headerName: 'Tank ID',
    field: 'tank_id',
  }
}

export const defaultColumnDefs = (
  showModal: (orderId: string) => void,
  scopes: Partial<Gravitate.AuthorizationResponse>,
  setOrderHistoryStoreId: (storeId: string) => void,
  setAuditOrderId: (orderId: string | undefined) => void,
  isCarrierUser: boolean,
  gridAPIRef: React.MutableRefObject<GridApi>,
  siteConfig: Gravitate.ModelConfig,
  setChangeId: React.Dispatch<React.SetStateAction<string | undefined>>,
  handleOrderChange: (newOrder: Gravitate.Order) => void,
  isCompact: boolean | undefined,
  triggerTPDModal: (orderId: string, carrier?: string) => void,
  handleTPDDismissModal: (order?: Gravitate.Order, shouldUpdate?: boolean, selectedCarrier?: string) => void,
  orderStatus: string,
  setIsPopoverVisible: React.Dispatch<React.SetStateAction<string>>,
  isPopoverVisible: string,
  manualPO?: boolean
): ColDef<Gravitate.Order>[] => {
  let columns = [
    IdColumn({ showModal }),
    OrderColumn(),
    ProductsColumn(),
    TotalVolumeColumn(),
    scopes?.is_carrier ? RecievedAtColumn() : UnresolvedFlagsColumn({ handleOrderChange }),
    MarketColumn(),
    SectorColumn(),
    DriverColumn(),
    DriverStatusColumn({ scopes }),
    HauledByColumn(),
    DestinationsColumn({ setOrderHistoryStoreId }),
    LocationNameColumn(),
    SiteNameColumn(),
    DestinationNameColumn(),
    DestinationStreetColumn(),
    DestinationCityColumn(),
    DestinationStateColumn(),
    DestinationPostalCodeColumn(),
    OriginsColumn(),
    OriginNameColumn(),
    OriginStreetColumn(),
    OriginCityColumn(),
    OriginStateColumn(),
    OriginPostalCodeColumn(),
    SuppliersColumn(),
    LoadingWindowColumn(),
    LoadingWindowStartColumn(),
    LoadingWindowEndColumn(),
    DeliveryWindowColumn(),
    DeliveryWindowStartColumn(),
    DeliveryWindowEndColumn(),
    ...lpWindowColumnDefs,
    RunoutColumn(),
    RunoutDateTimeColumn('date'),
    RunoutDateTimeColumn('time'),
    NoFitColumn(),
    FitDateTimeColumn('date'),
    FitDateTimeColumn('time'),
    PurchaseOrderColumn({ isPopoverVisible, manualPO, scopes, setIsPopoverVisible }),
    UpdatedColumn({ setAuditOrderId }),
    RejectColumn({ gridAPIRef, isCarrierUser }),
    EstDropTimeColumn(),
    DeliveryDateColumn(),
    DeliveryTimeColumn(),
    BOLDateColumn(),
    ...getPriceFlagColumnDefs({ handleOrderChange }),
    ContractColumn(),
    DriverNotesColumn(),
    LoadNumberColumn(),
    CustomerColumn(),
    SupplyLockedColumn(),
    LastSupplyAutoUpdateColumn(),
    CarrierWindowStartColumn(),
    CarrierWindowEndColumn(),
  ]

  if (siteConfig?.multi_truck) {
    const index = columns.findIndex((item) => item.field === 'products')
    columns.splice(index + 1, 0, TrailerColumn())
  }

  if (scopes?.order_management?.carrier_approve_updates) {
    const index = columns.findIndex((item) => item.field === 'sector')
    columns.splice(index + 1, 0, UpdatesColumn({ setChangeId }))
  }

  if (isCompact) {
    columns = [...columns, ActionsColumn({ gridAPIRef, handleTPDDismissModal, orderStatus, scopes, triggerTPDModal })]
  }

  return columns
}

export const sharedOrderDispatchDefs = [
  'status',
  'lp_window',
  'lp_start',
  'lp_end',
  'state',
  'delivery_date',
  'delivery_time',
  'bol_date',
  'price_flag',
  'price_flag.name',
  'price_flag.reason_code',
  'price_flag.updated_by',
  'price_flag.updated_date',
  'price_flag.price_difference',
  'price_flag.optimal_terminal',
  'price_flag.loaded_mile_difference',
]

export const detailsColumnDefs = (showModal?: (orderId: string) => void, isDetail?: boolean) => [
  IdColumn({ showModal }),
  OrderColumn(!showModal),
  ProductColumn(),
  PreDropUllage(),
  VolumeColumn(),
  TankIdColumn(),
  OriginColumn(),
  DestinationColumn(),
  DeliveryWindowStartColumn(),
  DeliveryWindowEndColumn(),
  DeliveryWindowColumn(isDetail),
  RunoutColumn(true),
  RunoutDateTimeColumn('date', true),
  RunoutDateTimeColumn('time', true),
  NoFitColumn(true),
  FitDateTimeColumn('date', true),
  FitDateTimeColumn('time', true),
]
